// src/components/SimpleComponents/SimpleCurrencyPercentage.jsx
// *******************************************************************************
// SIMPLE CURRENCY PERCENTAGE
// *******************************************************************************
// Hybrid component that allows input as either percentage or currency value.
// Maintains both values in sync based on a base reference value.
// Form data structure: { init: 'percentage'|'currency', percentage: number, value: number }
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Input, Select, Space } from 'antd';
import { useSimpleFormContext } from './SimpleFormProvider';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import useCustomValidationStatus from 'hooks/useCustomValidationStatus';
import useSubscriptionLocale from 'hooks/useSubscriptionLocale';

const { Option } = Select;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODE_PERCENTAGE = 'percentage';
const MODE_CURRENCY = 'currency';

const DEFAULT_CURRENCIES = [
  { value: 'BRL', label: 'R$', text: 'Brazilian Real' },
  { value: 'USD', label: '$', text: 'US Dollar' },
  { value: 'EUR', label: '\u20ac', text: 'Euro' },
];

const LOCALE_CONFIGS = {
  'pt-BR': { thousandSeparator: '.', decimalSeparator: ',' },
  'en-US': { thousandSeparator: ',', decimalSeparator: '.' },
  default: { thousandSeparator: ',', decimalSeparator: '.' },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleCurrencyPercentage = ({
  name,
  style = {},
  subdocumentField,

  // Base value for calculations (required for percentage <-> value conversion)
  base = 0,

  // Initial mode: 'percentage' or 'currency'
  init = MODE_PERCENTAGE,

  // Locale (undefined = use subscription locale automatically)
  locale,

  // Currency suffix options (undefined = auto-derive from subscription locale)
  currencies,
  'x-currency-style': xCurrencyStyle = { minWidth: 40 },

  // Percentage formatting
  percentageShowThousandSeparator = false,
  percentageShowDecimalSeparator = true,
  percentageCustomThousandSeparator,
  percentageCustomDecimalSeparator,
  percentageDecimalPlaces = 2,
  percentageMin = 0,
  percentageMax = 100,

  // Currency formatting
  currencyShowThousandSeparator = true,
  currencyShowDecimalSeparator = true,
  currencyCustomThousandSeparator,
  currencyCustomDecimalSeparator,
  currencyDecimalPlaces = 2,
  currencyMin = 0,
  currencyMax,

  // Rounding
  rounding = 'natural', // 'up' | 'down' | 'natural'

  // Other props
  disabled,
  onBlur,
  onChange,
  placeholder,

  ...props
}) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { locked, disabledFields, form } = useSimpleFormContext();

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;
  const { customValidationStatus, customValidationMessage } = useCustomValidationStatus({
    ...props,
    ...elementProps,
  });
  const subscriptionLocale = useSubscriptionLocale();

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);

  const effectiveCurrencies = (() => {
    const localeDef = subscriptionLocale.defaultCurrency;
    if (!currencies || currencies.length === 0) {
      const match = DEFAULT_CURRENCIES.find(c => c.value === localeDef);
      return match ? [match] : [DEFAULT_CURRENCIES[0]];
    }
    if (currencies.length === 1 && localeDef && currencies[0].value !== localeDef) {
      const match = DEFAULT_CURRENCIES.find(c => c.value === localeDef);
      if (match) return [match];
    }
    return currencies;
  })();

  const localeConfig = locale
    ? LOCALE_CONFIGS[locale] || LOCALE_CONFIGS.default
    : { thousandSeparator: subscriptionLocale.thousandSeparator, decimalSeparator: subscriptionLocale.decimalSeparator };

  // Get separators based on current mode
  const getFormatConfig = useCallback(
    mode => {
      if (mode === MODE_PERCENTAGE) {
        return {
          thousandSep: percentageCustomThousandSeparator !== undefined
            ? percentageCustomThousandSeparator
            : percentageShowThousandSeparator
              ? localeConfig.thousandSeparator
              : '',
          decimalSep: percentageCustomDecimalSeparator !== undefined
            ? percentageCustomDecimalSeparator
            : percentageShowDecimalSeparator
              ? localeConfig.decimalSeparator
              : '',
          decimalPlaces: percentageDecimalPlaces,
          min: percentageMin,
          max: percentageMax,
        };
      }
      return {
        thousandSep: currencyCustomThousandSeparator !== undefined
          ? currencyCustomThousandSeparator
          : currencyShowThousandSeparator
            ? localeConfig.thousandSeparator
            : '',
        decimalSep: currencyCustomDecimalSeparator !== undefined
          ? currencyCustomDecimalSeparator
          : currencyShowDecimalSeparator
            ? localeConfig.decimalSeparator
            : '',
        decimalPlaces: currencyDecimalPlaces,
        min: currencyMin,
        max: currencyMax ?? (base || 999999999.99),
      };
    },
    [
      localeConfig,
      percentageShowThousandSeparator,
      percentageShowDecimalSeparator,
      percentageCustomThousandSeparator,
      percentageCustomDecimalSeparator,
      percentageDecimalPlaces,
      percentageMin,
      percentageMax,
      currencyShowThousandSeparator,
      currencyShowDecimalSeparator,
      currencyCustomThousandSeparator,
      currencyCustomDecimalSeparator,
      currencyDecimalPlaces,
      currencyMin,
      currencyMax,
      base,
    ]
  );

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [displayValue, setDisplayValue] = useState('');
  const [currentMode, setCurrentMode] = useState(init);
  const [internalPercentage, setInternalPercentage] = useState(0);
  const [internalValue, setInternalValue] = useState(0);
  const isFocusedRef = useRef(false);
  const isInitializedRef = useRef(false);

  // Watch form value changes
  const watchedValue = Form.useWatch(fullFieldName, form);

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------
  // Initialize from form value or defaults
  useEffect(() => {
    if (isInitializedRef.current && isFocusedRef.current) return;

    if (watchedValue && typeof watchedValue === 'object') {
      // Load from existing form data
      const savedInit = watchedValue.init || init;
      const savedPercentage = watchedValue.percentage ?? 0;
      const savedValue = watchedValue.value ?? 0;

      setCurrentMode(savedInit);
      setInternalPercentage(savedPercentage);
      setInternalValue(savedValue);

      // Update display based on mode
      const config = getFormatConfig(savedInit);
      const valueToDisplay = savedInit === MODE_PERCENTAGE ? savedPercentage : savedValue;
      const formattedValue = formatNumber(valueToDisplay, config);
      setDisplayValue(formattedValue);

      isInitializedRef.current = true;
    } else if (!isInitializedRef.current) {
      // Initialize with defaults
      setCurrentMode(init);
      setInternalPercentage(0);
      setInternalValue(0);
      setDisplayValue('');
      isInitializedRef.current = true;
    }
  }, [watchedValue, init, getFormatConfig]);

  // Recalculate value when base changes (if we have a percentage)
  useEffect(() => {
    if (base > 0 && internalPercentage > 0) {
      const newValue = applyRounding((base * internalPercentage) / 100, currencyDecimalPlaces, rounding);
      setInternalValue(newValue);

      // Update form with new calculated value
      updateFormValue(currentMode, internalPercentage, newValue);
    }
  }, [base]);

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const updateFormValue = useCallback(
    (mode, percentage, value) => {
      const formData = {
        init: mode,
        percentage: applyRounding(percentage, percentageDecimalPlaces, rounding),
        value: applyRounding(value, currencyDecimalPlaces, rounding),
      };

      form?.setFieldValue(fullFieldName, formData);
      onChange?.(formData);
    },
    [form, fullFieldName, onChange, percentageDecimalPlaces, currencyDecimalPlaces, rounding]
  );

  const handleInputChange = useCallback(
    ev => {
      const rawValue = ev?.target?.value ?? '';
      const config = getFormatConfig(currentMode);

      // Allow typing: only digits and decimal separator
      const regex = new RegExp(`^[0-9${escapeRegex(config.decimalSep)}]*$`);
      if (!regex.test(rawValue) && rawValue !== '') return;

      // Prevent multiple decimal separators
      if (config.decimalSep) {
        const sepCount = (rawValue.match(new RegExp(escapeRegex(config.decimalSep), 'g')) || []).length;
        if (sepCount > 1) return;
      }

      // Update display value while typing
      setDisplayValue(rawValue);

      // Parse numeric value
      const numericValue = parseLocaleNumber(rawValue, config.decimalSep);

      if (!isNaN(numericValue) && rawValue !== '') {
        if (currentMode === MODE_PERCENTAGE) {
          // User is typing percentage - calculate value
          const calculatedValue = base > 0 ? (base * numericValue) / 100 : 0;
          setInternalPercentage(numericValue);
          setInternalValue(calculatedValue);
          updateFormValue(currentMode, numericValue, calculatedValue);
        } else {
          // User is typing value - calculate percentage
          const calculatedPercentage = base > 0 ? (numericValue / base) * 100 : 0;
          setInternalValue(numericValue);
          setInternalPercentage(calculatedPercentage);
          updateFormValue(currentMode, calculatedPercentage, numericValue);
        }
      } else if (rawValue === '') {
        setInternalPercentage(0);
        setInternalValue(0);
        updateFormValue(currentMode, 0, 0);
      }
    },
    [currentMode, base, getFormatConfig, updateFormValue]
  );

  const handleBlur = useCallback(
    ev => {
      isFocusedRef.current = false;
      const rawValue = ev?.target?.value ?? '';
      const config = getFormatConfig(currentMode);

      if (rawValue === '') {
        setDisplayValue('');
        onBlur?.(ev);
        return;
      }

      // Parse current value
      const cleanValue = rawValue.replace(new RegExp(`[${escapeRegex(config.thousandSep)}]`, 'g'), '');
      let numericValue = parseLocaleNumber(cleanValue, config.decimalSep);

      if (isNaN(numericValue)) {
        setDisplayValue('');
        updateFormValue(currentMode, 0, 0);
        onBlur?.(ev);
        return;
      }

      // Apply min/max
      if (config.min !== undefined && numericValue < config.min) numericValue = config.min;
      if (config.max !== undefined && numericValue > config.max) numericValue = config.max;

      // Apply rounding
      numericValue = applyRounding(numericValue, config.decimalPlaces, rounding);

      // Update internal state and form
      if (currentMode === MODE_PERCENTAGE) {
        const calculatedValue = base > 0
          ? applyRounding((base * numericValue) / 100, currencyDecimalPlaces, rounding)
          : 0;
        setInternalPercentage(numericValue);
        setInternalValue(calculatedValue);
        updateFormValue(currentMode, numericValue, calculatedValue);
      } else {
        const calculatedPercentage = base > 0
          ? applyRounding((numericValue / base) * 100, percentageDecimalPlaces, rounding)
          : 0;
        setInternalValue(numericValue);
        setInternalPercentage(calculatedPercentage);
        updateFormValue(currentMode, calculatedPercentage, numericValue);
      }

      // Format display
      const formattedValue = formatNumber(numericValue, config);
      setDisplayValue(formattedValue);

      onBlur?.(ev);
    },
    [currentMode, base, getFormatConfig, updateFormValue, rounding, currencyDecimalPlaces, percentageDecimalPlaces, onBlur]
  );

  const handleFocus = useCallback(
    ev => {
      isFocusedRef.current = true;
      const config = getFormatConfig(currentMode);

      // Remove thousand separators for easier editing
      const currentValue = ev?.target?.value ?? '';
      if (config.thousandSep && currentValue.includes(config.thousandSep)) {
        const cleanValue = currentValue.replace(new RegExp(escapeRegex(config.thousandSep), 'g'), '');
        setDisplayValue(cleanValue);
      }

      // Select all text on focus
      setTimeout(() => {
        ev?.target?.select?.();
      }, 0);
    },
    [currentMode, getFormatConfig]
  );

  const handleModeChange = useCallback(
    newMode => {
      setCurrentMode(newMode);

      // Get the value to display based on new mode
      const valueToDisplay = newMode === MODE_PERCENTAGE ? internalPercentage : internalValue;
      const config = getFormatConfig(newMode);
      const formattedValue = formatNumber(valueToDisplay, config);
      setDisplayValue(formattedValue);

      // Update form with new init mode
      updateFormValue(newMode, internalPercentage, internalValue);
    },
    [internalPercentage, internalValue, getFormatConfig, updateFormValue]
  );

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------
  const config = getFormatConfig(currentMode);
  const inputStyle = { flex: 1, height: 40, textAlign: 'right', ...style };

  // Build suffix options (percentage + currencies)
  const suffixOptions = useMemo(() => {
    const options = [{ value: MODE_PERCENTAGE, label: '%', text: 'Percentage' }];

    effectiveCurrencies.forEach(currency => {
      options.push({
        value: MODE_CURRENCY,
        label: currency.label,
        text: currency.text,
        currencyValue: currency.value,
      });
    });

    return options;
  }, [effectiveCurrencies]);

  // Build suffix element
  const suffixElement = useMemo(() => {
    const currentOption = currentMode === MODE_PERCENTAGE
      ? suffixOptions[0]
      : suffixOptions.find(o => o.value === MODE_CURRENCY) || suffixOptions[1];

    if (suffixOptions.length === 2) {
      // Just percentage and one currency - show toggle (wider to accommodate dropdown arrow)
      return (
        <Select
          disabled={isDisabled}
          value={currentMode}
          onChange={handleModeChange}
          style={{ width: 70, minWidth: 70, ...xCurrencyStyle }}
          getPopupContainer={trigger => trigger.parentNode}
        >
          {suffixOptions.map(opt => (
            <Option key={opt.value} value={opt.value} title={opt.text}>
              {opt.label}
            </Option>
          ))}
        </Select>
      );
    }

    // Multiple currencies - show select with all options (wider to accommodate dropdown arrow)
    return (
      <Select
        disabled={isDisabled}
        value={currentMode}
        onChange={handleModeChange}
        style={{ width: 70, minWidth: 70, ...xCurrencyStyle }}
        getPopupContainer={trigger => trigger.parentNode}
      >
        {suffixOptions.map((opt, idx) => (
          <Option key={`${opt.value}-${idx}`} value={opt.value} title={opt.text}>
            {opt.label}
          </Option>
        ))}
      </Select>
    );
  }, [suffixOptions, currentMode, isDisabled, handleModeChange, xCurrencyStyle]);

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  const inputElement = (
    <Input
      ref={elementRef}
      id={flattenFieldName}
      style={inputStyle}
      value={displayValue}
      onChange={handleInputChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      disabled={isDisabled}
      placeholder={placeholder ?? '0' + config.decimalSep + '0'.repeat(config.decimalPlaces)}
    />
  );

  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      customValidationStatus={customValidationStatus}
      customValidationMessage={customValidationMessage}
    >
      <Space.Compact style={{ width: '100%' }}>
        {inputElement}
        {suffixElement}
      </Space.Compact>
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleCurrencyPercentage;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function escapeRegex(str) {
  if (!str) return '';
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function parseLocaleNumber(value, decimalSep) {
  if (!value || value === '') return NaN;
  const normalized = value.replace(decimalSep, '.');
  return parseFloat(normalized);
}

function applyRounding(value, decimalPlaces, rounding) {
  if (isNaN(value)) return 0;

  const multiplier = Math.pow(10, decimalPlaces);

  switch (rounding) {
    case 'up':
      return Math.ceil(value * multiplier) / multiplier;
    case 'down':
      return Math.floor(value * multiplier) / multiplier;
    case 'natural':
    default:
      return Math.round(value * multiplier) / multiplier;
  }
}

function formatNumber(value, { thousandSep, decimalSep, decimalPlaces }) {
  if (value === null || value === undefined) return '';

  // Ensure value is a number
  const numValue = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(numValue)) return '';

  const parts = numValue.toFixed(decimalPlaces).split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1] || '';

  let formattedInteger = integerPart;
  if (thousandSep) {
    formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
  }

  if (decimalPlaces > 0 && decimalSep) {
    return `${formattedInteger}${decimalSep}${decimalPart}`;
  }

  return formattedInteger;
}
